import { useState } from 'react';
import type { ClosedDate } from '@salonomy/types';
import { Button, Card, Field, TextInput } from '@salonomy/ui';
import { useClosedDates, useCreateClosedDate, useDeleteClosedDate } from '../../data/scheduling.js';
import { longDate, todayInZone } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS7 phase-1 — salon-wide closed dates (bank holidays, refits). Manager-only (mirrors the API).
 * Closing a date renders the diary's closed-day banner and suppresses bookable gaps for that day.
 */
export function ClosedDatesPanel({ tz }: { tz: string }) {
  const toast = useToast();
  const { data: dates = [] } = useClosedDates();
  const create = useCreateClosedDate();
  const remove = useDeleteClosedDate();

  const today = todayInZone(tz);
  const [date, setDate] = useState(today);
  const [reason, setReason] = useState('');

  async function add() {
    if (!date) {
      toast.error('Pick a date to close.');
      return;
    }
    try {
      await create.mutateAsync({ date, reason: reason.trim() || null });
      toast.success('Closed date added');
      setReason('');
    } catch {
      toast.error('Could not add this closed date.');
    }
  }

  async function del(d: ClosedDate) {
    try {
      await remove.mutateAsync(d.id);
      toast.success('Re-opened');
    } catch {
      toast.error('Could not re-open this date.');
    }
  }

  const sorted = [...dates].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Card>
      <div className="section-title">Closed dates</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        Bank holidays and refits — the salon takes no bookings on these dates.
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.5fr auto',
          gap: 12,
          marginTop: 12,
          alignItems: 'end',
        }}
      >
        <Field label="Date">
          <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Reason" hint="Optional">
          <TextInput
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Bank holiday"
          />
        </Field>
        <Button onClick={add} disabled={create.isPending}>
          Add
        </Button>
      </div>

      {sorted.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          No closed dates set.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 8 }}>
          {sorted.map((d) => (
            <div key={d.id} className="settings-row">
              <div>
                <div style={{ fontWeight: 600 }}>{longDate(d.date, tz)}</div>
                {d.reason ? (
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {d.reason}
                  </div>
                ) : null}
              </div>
              <Button size="sm" variant="ghost" onClick={() => del(d)} disabled={remove.isPending}>
                Re-open
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
